<template>
  <BaseModal
    :title="$t('Changes')"
    :tabs="lists"
    :current-tab-id="currentTabId"
    @change-tab="handleTabChanging"
  >
    <template #default>
      <div v-if="isChangesLoading" class="flex-centering">
        <Spinner size="large" />
      </div>
      <ScrollPagination
        v-else-if="changesByList.length"
        :items="changesByList"
        :url="nextUrl"
        @upload="addMoreChanges"
      >
        <div v-for="(changesItem, idx) in changesByList" :key="idx" class="changes-box_container">
          <HistoryBlock
            :item="changesItem"
            :is-last-block="idx === changesByList.length - 1"
            :current-list="currentTabId"
            :publishing="publishLoading"
            :file-may-be-not-uploaded="
              get(changesItem, 'id', false) && changesWithoutFiles.includes(changesItem.id)
            "
            @publish="publishChanges"
          />
        </div>
      </ScrollPagination>
      <TenderPlug
        v-else
        :title="$t('No changes')"
        :description="$t('Any changes of items will appear here.')"
        icon="list"
        light
        large
      />
    </template>
    <template #footer>
      <div class="flex-justify-between width-100">
        <Button type="func" left-icon="info-circle">{{ $t('How it works?') }}</Button>
        <div class="spacex-12">
          <Button type="secondary" @click="closeModal">{{ $t('Close') }}</Button>
          <Button type="primary" @click="publishChanges">{{ $t('Publish changes') }}</Button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
  import BaseModal from '@/components/ui/BaseModal';
  import Button from '@/components/ui-v2/Button';
  import HistoryBlock from '@/components/project/history/HistoryBlock';
  import { mapActions, mapState } from 'vuex';
  import factory from '@/utils/api/factory';
  import AffectsTenders from '@/components/project/history/AffectsTenders';
  import ScrollPagination from '@/containers/common/ScrollPagination';
  import Spinner from '@/components/ui/Spinner';
  import projectChangesCount from '@/gql/projects/project-changes.graphql';
  import get from 'lodash.get';
  import TenderPlug from '@/components/tender/TenderPlug';

  const DEFAULT_TAB_ID = 0;

  export default {
    name: 'ProjectChangesModal',
    components: {
      BaseModal,
      Button,
      HistoryBlock,
      ScrollPagination,
      Spinner,
      TenderPlug,
    },
    async created() {
      this.isChangesLoading = true;
      await this.updateChanges();
      this.isChangesLoading = false;
    },
    data() {
      return {
        publishedChanges: [],
        currentTabId: DEFAULT_TAB_ID,
        nextUrl: null,
        publishLoading: false,
        isChangesLoading: false,
        changesWithoutFiles: [],
      };
    },
    apollo: {
      $subscribe: {
        changes() {
          return {
            query: projectChangesCount,
            variables() {
              return {
                id: this.project.id,
              };
            },
            async result({ data }) {
              if (data.project_change_aggregate.aggregate.count && !this.publishLoading) {
                await this.updateChanges(this.publishedChanges.length);
              }
            },
          };
        },
      },
    },
    computed: {
      ...mapState('modal', ['context']),
      ...mapState('auth', ['user']),
      changesByList() {
        return this.showAllHistory ? this.publishedChanges : this.filterChanges();
      },
      showAllHistory() {
        return this.currentTabId === DEFAULT_TAB_ID;
      },
      project() {
        return this.context?.project || { version: [] };
      },
      lists() {
        return [
          {
            id: 0,
            text: this.$t('All changes'),
          },
          ...this.project.lists.map((list) => {
            return {
              id: list.id,
              text: list.title,
            };
          }),
        ];
      },
    },
    methods: {
      get,
      ...mapActions('confirmModal', ['openModal']),
      ...mapActions('modal', ['closeModal']),
      ...mapActions('alert', ['showAlert']),
      filterChanges() {
        return this.publishedChanges
          .map((item) => {
            return {
              ...item,
              changes: item?.changes.filter((change) => change.list.id === this.currentTabId),
            };
          })
          .filter((item) => item.changes.length);
      },
      async publishChanges() {
        let { data } = await factory('projects').indexInPath(
          `${this.context.projectId}/changes/affects_tenders`
        );
        this.openModal({
          title: this.$t('Publish changes?'),
          actionText: this.$t('Publish'),
          component: AffectsTenders,
          tenders: data,
          action: async () => {
            try {
              this.publishLoading = true;
              let { data } = await factory('projects').postInResourcePath(
                this.context.projectId,
                'changes/publish'
              );
              this.publishedChanges[0] = data;
              this.changesWithoutFiles.push(data.id);
              this.publishLoading = false;
            } catch (e) {
              console.error(e);
              this.showAlert({
                type: 'error',
                text: e.response.data,
              });
            }
          },
        });
      },
      handleTabChanging(id) {
        this.currentTabId = id;
      },
      async updateChanges(limit = null) {
        try {
          let { data } = await factory('projects').indexInPath(
            `${this.context.projectId}/changes`,
            {
              limit,
            }
          );

          this.publishedChanges = data?.not_published_changes?.length
            ? [
                {
                  changes: data.not_published_changes,
                },
                ...data.results,
              ]
            : data.results;

          this.nextUrl = data.next;
        } catch (e) {
          console.error(e);
          this.showAlert({
            type: 'error',
            text: e.response.data,
          });
        }
      },
      addMoreChanges(items) {
        this.publishedChanges = items;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .vertical-row {
    background-color: #000;
    display: inline-block;
    margin: 0;
    height: 2px;
    width: 1px;
  }
</style>
